import { useResourceRelationship, useResources } from 'datx-jsonapi-react';
import { FC, useCallback, useMemo } from 'react';

import { Skeleton } from '@/components/core/Skeleton/Skeleton';
import { DropdownMultiSelect } from '@/components/ui/shared/DropdownMultiSelect/DropdownMultiSelect';
import { IOption } from '@/components/ui/shared/DropdownSelect/DropdownSelect';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { queryAllTracks } from '@/queries/tracks';
import { EventConfiguration } from '@/resources/EventConfiguration';

interface IUrlQueryStringParams {
  eventId?: string;
  trackIds?: string;
}

export enum TrackFilterScope {
  Lectures = 'with_lectures',
  Posters = 'with_posters',
}

interface IScheduleTrackFilterProps {
  scope?: TrackFilterScope;
  posterOnlySessions?: boolean;
}

export const ScheduleTrackFilter: FC<IScheduleTrackFilterProps> = ({ scope, posterOnlySessions }) => {
  const { eventId, trackIds, change } = useQueryParams<IUrlQueryStringParams>({
    eventId: StringParam,
    trackIds: StringParam,
  });

  const { data: eventConfiguration } = useResourceRelationship<EventConfiguration>(
    queryThinEventConfigurationRelationship(eventId)
  );

  const trackIdsArray = trackIds?.split(',') || [];

  const { data: tracks } = useResources(
    queryAllTracks({ event_id: eventId, scope, poster_only_sessions: posterOnlySessions }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const items = useMemo(() => tracks && tracks.map((t) => ({ id: t.id, value: t.name })), [tracks]);
  const selectedOptions = useMemo(() => (items || []).filter((item) => trackIdsArray.find((tf) => tf === item.id)), [
    items,
    trackIds,
  ]);

  const onChange = useCallback(
    (trackOption: IOption) => {
      const idInUrl = trackIdsArray.find((ta) => ta === trackOption.id);
      let newTrackIds = [];

      if (idInUrl) {
        newTrackIds = trackIdsArray.filter((ta) => ta !== trackOption.id);
      } else {
        newTrackIds = [...trackIdsArray, trackOption.id];
      }

      if (trackOption.id === null) {
        newTrackIds = [];
      }

      const urlFormatted = newTrackIds.length !== 0 ? newTrackIds.join(',') : undefined;

      change({ trackIds: urlFormatted }, { method: 'replace', shallow: true });
    },
    [change, trackIds]
  );

  if (!tracks) {
    return <Skeleton css={{ width: '200px', height: '40px' }} />;
  }

  return (
    <DropdownMultiSelect
      defaultLabel={eventConfiguration?.tracksLabelName || 'all tracks'}
      maxW="200px"
      height={{ base: '42px' }}
      options={items}
      onClickOption={onChange}
      selectedOptions={selectedOptions}
    />
  );
};
